import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { ReactComponent as ChevronRight } from '../../assets/icons/chevron-right.svg';
import ChatContext from '../../context/ChatContext';
import FirebaseContext from '../../context/FirebaseContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import colors from '../../styles/colors';
import { Event } from '../../utils/events';
import useComponentMounted from '../../hooks/useComponentMounted';
import useToast from '../../hooks/useToast';
import SmartContextHistory from '../../Model/SmartContextHistory';
import SmartContextResponsesCache, {
  SmartContextResponsesCacheStatus,
} from '../../../CoachTools/Model/SmartContextResponsesCache';
import { applySkinToneToText } from '../../../utils/emoji';
import {
  Header,
  HideButton,
  Button,
  ResizableBox,
  PromptBox,
  SmartContextContainer,
  SmartContextMessage,
  Body,
  Loader,
  Title,
  Reason,
  ClientFacts,
  DisplayToggleWrapper,
  InsightsGained,
  EndButtonContainer,
  StyledBox,
  Badge,
  AdditionalQueryContainer,
  QueryContent,
} from './styles';

import texts from './texts.json';
import MultiResponsePreview from './MultiResponsePreview/MultiResponsePreview';
import { SmartContextErrorLevel, SmartContextErrorTypes } from './constants';

const TABS = [
  {
    id: 'reasoning',
    label: 'Reasoning',
  },
  {
    id: 'client-facts',
    label: 'Client Facts',
  },
  {
    id: 'insights-gained',
    label: 'Insights Gained',
  },
];

const SmartContext = ({ onInsertClick }) => {
  const {
    authUser, smartContextOpen, setSmartContextOpen, logEvent,
  } = useContext(ChatContext);
  const {
    firebase: { remote },
  } = useContext(FirebaseContext);
  const {
    activeChannel, coachDoc, handleAddToScheduleQueue, scheduledMessageIds,
  } = useContext(ChatChannelContext);
  const { showToast } = useToast();

  const [selectedTab, setSelectedTab] = useState(TABS[0].id);
  const [smartResponse, setSmartResponse] = useState();
  const [smartContextLoading, setSmartContextLoading] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [additionalQueryInSR, setAdditionalQueryInSR] = useState('');
  const [traceIdInSR, setTraceIdInSR] = useState('');
  const isComponentMountedRef = useComponentMounted();
  const [showAdditionalQuery, setShowAdditionalQuery] = useState(false);
  const [multiResponses, setMultiResponses] = useState({});
  const [isMultiResponse, setIsMultiResponse] = useState(false);
  const [hasAutoInserted, setHasAutoInserted] = useState(false);

  const sessionId = useMemo(() => {
    if (activeChannel && authUser?.id) {
      return `${activeChannel.id}-${authUser.id}-${Date.now()}`;
    }
    return null;
  }, [activeChannel, authUser]);

  const handleSetSmartResponse = useCallback((data) => {
    if (data.response && data.response.type === 'multi' && data.response.messages) {
      setIsMultiResponse(true);
      setMultiResponses(data.response.messages);
      setSmartResponse(data);
    } else {
      setIsMultiResponse(false);
      setMultiResponses({});
      setSmartResponse(data);
    }
    // Reset auto-insert flag when new response is loaded
    setHasAutoInserted(false);
  }, []);

  const removeResponse = (messageId) => {
    setMultiResponses((prev) => prev.filter((resp) => resp.messageId !== messageId));
  };

  const fetchSmartContext = useCallback(
    async (clientId, additionalQuery = '', forceRefresh = false) => {
      if (!clientId) return;
      try {
        setSmartContextLoading(true);
        if (!smartContextOpen || !activeChannel?.id || !authUser?.id || !sessionId) {
          return;
        }

        // Check if we already generated a smart context message
        const smartContextResponsesCache = await SmartContextResponsesCache
          .getSmartContextResponsesCacheByChannelId(activeChannel.id);

        if (
          !forceRefresh
          && smartContextResponsesCache.exists
          && smartContextResponsesCache.status === SmartContextResponsesCacheStatus.FRESH
        ) {
          handleSetSmartResponse(smartContextResponsesCache.docData);
          setTraceIdInSR(smartContextResponsesCache.traceId || '');
        } else {
          // We have not pre-generated a smart context message, so we need to generate one.
          const response = await remote('smartContext-generate', {
            userId: clientId,
            channelId: activeChannel.id,
            additionalQuery,
            forceRefresh,
          });

          // If the response is not ok, throw an error
          if (!response.ok) {
            const { error, type } = await response.json();
            throw new Error(error || response.statusText, { cause: type });
          }

          const { data, additionalQuery: additionalQueryFromSR, traceId: traceIdFromSR } = await response.json();
          handleSetSmartResponse(data);
          setAdditionalQueryInSR(additionalQueryFromSR);
          setTraceIdInSR(traceIdFromSR);
          await SmartContextHistory.addSession({
            sessionId,
            response: data,
            query: additionalQuery,
            channelId: activeChannel.id,
            userId: authUser?.id || '',
          });

          setShowAdditionalQuery(false);
          setUserQuery('');
        }

        logEvent(Event.SMART_CONTEXT_VIEWED, {
          userId: authUser?.id,
          timestamp: new Date().toISOString(),
        });
        setSelectedTab(TABS[0].id);
      } catch (error) {
        const errorLevel = (error.cause && SmartContextErrorTypes[error.cause]?.level) || SmartContextErrorLevel.ERROR;
        showToast(error?.message || 'Error fetching AI reasoning', {
          ...(errorLevel === SmartContextErrorLevel.ERROR && { error: true }),
          ...(errorLevel === SmartContextErrorLevel.WARNING && { warning: true }),
        });
      } finally {
        setSmartContextLoading(false);
      }
    },
    [smartContextOpen, remote, logEvent, authUser.id, sessionId, activeChannel, showToast, handleSetSmartResponse],
  );

  useEffect(() => {
    if (!activeChannel?.userId || !isComponentMountedRef.current) return;
    fetchSmartContext(activeChannel.userId);
  }, [activeChannel.userId, fetchSmartContext, isComponentMountedRef]);

  // Auto-insert effect: Trigger auto-insert when response is loaded (only once)
  // Wait for coachDoc to be loaded to ensure emojisSkinTone is available
  useEffect(() => {
    if (
      smartResponse
      && !hasAutoInserted
      && !smartContextLoading
      && !isMultiResponse
      && smartResponse.response?.messages?.[0]?.response
      && coachDoc // Ensure coachDoc is loaded before auto-inserting
    ) {
      const responseText = applySkinToneToText(
        smartResponse.response.messages[0].response,
        coachDoc.emojisSkinTone,
      );

      // Call onInsertClick with isManual = false for auto-insert
      onInsertClick(responseText, additionalQueryInSR, false, traceIdInSR);
      setHasAutoInserted(true);
    }
  }, [
    smartResponse,
    hasAutoInserted,
    smartContextLoading,
    isMultiResponse,
    onInsertClick,
    additionalQueryInSR,
    coachDoc,
    traceIdInSR,
  ]);

  const toggleAdditionalQueryVisibility = useCallback(() => {
    setShowAdditionalQuery(!showAdditionalQuery);
  }, [showAdditionalQuery]);

  const loading = smartContextLoading;

  const handleCopy = useCallback(async (text) => {
    await navigator.clipboard.writeText(text);
    showToast(texts.copiedSuccessfully, { type: 'success' });
    logEvent(Event.SMART_CONTEXT_COPIED, {
      userId: authUser?.id,
      timestamp: new Date().toISOString(),
    });
  }, [authUser.id, logEvent, showToast]);

  return (
    <SmartContextContainer>
      <Header>
        <div>{texts.heading}</div>
        {smartContextOpen && (
          <HideButton
            title="Hide Smart Context"
            type="button"
            onClick={() => {
              setSmartContextOpen(false);
              logEvent(Event.SMART_CONTEXT_SIDEBAR_CLOSED, {
                userId: authUser?.id,
                timestamp: new Date().toISOString(),
                channelId: activeChannel?.id,
              });
            }}
          >
            <ChevronRight />
          </HideButton>
        )}
      </Header>
      <Body>
        {loading && <Loader fullHeight={false} />}
        {!loading && smartResponse && (
          <>
            <DisplayToggleWrapper>
              <label htmlFor="ai-detail-view-select">
                {texts.viewToggleLabel}
              </label>
              <select
                id="ai-detail-view-select"
                onChange={(e) => {
                  setSelectedTab(e.currentTarget.value);
                  logEvent(Event.SMART_CONTEXT_OPTION_CHANGED, {
                    userId: authUser?.id,
                    timestamp: new Date().toISOString(),
                    option: e.currentTarget.value,
                    channelId: activeChannel?.id,
                  });
                }}
              >
                {TABS.map((tab) => (
                  <option key={tab.id} value={tab.id}>
                    {tab.label}
                  </option>
                ))}
              </select>
            </DisplayToggleWrapper>

            {selectedTab === TABS[0].id && (
              <ResizableBox>
                <Title>{texts.aiReasoning}</Title>
                <Reason>{smartResponse.reason}</Reason>
              </ResizableBox>
            )}

            {selectedTab === TABS[1].id && (
              <ResizableBox>
                <Title>{texts.clientFacts}</Title>
                {smartResponse.clientFacts.length === 0 && (
                  <Reason>No facts were returned</Reason>
                )}
                {smartResponse.clientFacts.length > 0 && (
                  <ClientFacts>
                    {smartResponse.clientFacts.map((fact) => (
                      <Reason>{fact}</Reason>
                    ))}
                  </ClientFacts>
                )}
              </ResizableBox>
            )}

            {selectedTab === TABS[2].id && (
              <ResizableBox>
                <Title>{texts.insightsGained}</Title>
                <InsightsGained>
                  {Object.keys(smartResponse.insightsGained).map((insight) => (
                    <div key={insight}>
                      <div>{insight}</div>
                      <div>{smartResponse.insightsGained[insight]}</div>
                    </div>
                  ))}
                </InsightsGained>
              </ResizableBox>
            )}

            <PromptBox>
              <Title>{texts.response}</Title>
              {isMultiResponse ? (
                <MultiResponsePreview
                  multiResponses={multiResponses}
                  smartResponse={smartResponse}
                  traceIdInSR={traceIdInSR}
                  additionalQueryInSR={additionalQueryInSR}
                  onInsertClick={onInsertClick}
                  onCopyClick={handleCopy}
                  removeResponse={removeResponse}
                  onScheduleClick={handleAddToScheduleQueue}
                  scheduledMessageIds={scheduledMessageIds}
                />
              ) : (
                <Reason>
                  {
                    applySkinToneToText(
                      smartResponse.response?.messages?.[0]?.response,
                      coachDoc?.emojisSkinTone,
                    )
                  }
                </Reason>
              )}
              {additionalQueryInSR && (
                <AdditionalQueryContainer>
                  <Badge onClick={toggleAdditionalQueryVisibility}>
                    {texts.badgeText}
                  </Badge>
                  {showAdditionalQuery && (
                  <QueryContent>
                    {additionalQueryInSR}
                  </QueryContent>
                  )}
                </AdditionalQueryContainer>
              )}
            </PromptBox>
            <StyledBox minHeight={120}>
              <Title>{texts.additionalQuery}</Title>
              <SmartContextMessage
                disabled={loading}
                draggable={false}
                value={userQuery}
                onChange={(e) => setUserQuery(e.currentTarget.value)}
                placeholder={texts.additionalQueryPlaceholder}
              />

            </StyledBox>
          </>
        )}
      </Body>
      <EndButtonContainer>
        <div>
          <Button
            disabled={loading || isMultiResponse}
            backgroundColor={colors.base.secondary2}
            padding="8px"
            onClick={() => onInsertClick(
              applySkinToneToText(
                smartResponse?.response?.messages?.[0]?.response,
                coachDoc?.emojisSkinTone,
              ),
              additionalQueryInSR,
              true, // isManual = true for manual button click
              traceIdInSR,
            )}
          >
            {texts.insertButton}
          </Button>
        </div>
        <div>
          <Button
            disabled={loading}
            backgroundColor={colors.base.secondary2}
            padding="8px"
            onClick={async () => {
              const query = userQuery.trim();
              logEvent(Event.SMART_CONTEXT_REGENERATE, {
                userId: authUser?.id,
                timestamp: new Date().toISOString(),
                query,
                channelId: activeChannel?.id,
              });
              await fetchSmartContext(activeChannel?.userId, query, true);
            }}
          >
            {texts.regenerateButton}
          </Button>
        </div>
      </EndButtonContainer>

    </SmartContextContainer>
  );
};

SmartContext.propTypes = {
  onInsertClick: PropTypes.func.isRequired,
};

export default observer(SmartContext);
